import {
  Children,
  type ComponentType,
  isValidElement,
  memo,
  type PropsWithChildren,
  type ReactElement,
  useMemo,
} from 'react';

import type { LooseRouteParams } from '@routes/types';

import type { RouteContextType } from './router.context';
import { useRouteContext } from './router.hooks';

export interface PreparedRoute<
  Params extends LooseRouteParams = LooseRouteParams,
> {
  Component: ComponentType;
  condition?: (context: RouteContextType<Params>) => boolean;
  isDefault?: boolean;
}

interface MatchedRoute {
  condition?: (context: RouteContextType) => boolean;
  element: ReactElement;
  isDefault?: boolean;
}

const notFoundRoute: MatchedRoute = { element: <div>Not found</div> };

export const Route = <Params extends LooseRouteParams = LooseRouteParams>(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  props: PropsWithChildren<Partial<Omit<PreparedRoute<Params>, 'isDefault'>>>,
) => null;

/**
 * Provides a compositional mechanism for switching parts of a layout based on
 * specifics of the current route. Define each variant with `Route` children.
 *
 * @see Route
 *
 * @example
 * ```tsx
 * <Routes>
 *   <Route
 *     condition={({ params }) => 'id' in params}
 *     Component={ExampleDetail}
 *   />
 *   <Route Component={ExampleIndex} />
 * <Routes>
 * ```
 */
export const Routes = memo(({ children }: PropsWithChildren<unknown>) => {
  const context = useRouteContext();

  const validRoutes = useMemo(
    () =>
      Children.map(Children.toArray(children), child => {
        if (!isValidElement(child)) {
          return null;
        }

        if (child.type !== Route) {
          console.warn('Routes may only contain Route children.');
          return null;
        }

        const { Component, condition, children: routeChildren } = child.props;

        if (!Component && !routeChildren) {
          console.warn('Route components must have a Component prop.');
          return null;
        }

        // Hold the child element itself rather than wrapping it in a
        // synthesized component, whose per-render function identity React
        // reconciles as a new element type, remounting the matched route.
        return {
          condition,
          element: routeChildren ? Children.only(routeChildren) : <Component />,
          isDefault: !condition,
        } as MatchedRoute;
      }),
    [children],
  );

  const defaultRoute =
    validRoutes.find(route => route.isDefault) ?? notFoundRoute;

  const matchedRoute = validRoutes.find(route => route.condition?.(context));

  return (matchedRoute ?? defaultRoute).element;
});
Routes.displayName = 'Routes';

export default Routes;
